// Armado de las filas del export de instrucciones operativas.
//
// Es el listado de trabajo de Middle Office, no un archivo de carga de un
// sistema externo: las columnas siguen a la tabla de la pantalla y en el mismo
// orden, salvo tres que en la UI van embebidas en otra celda y en una planilla
// conviene tener aparte ("Vía Egreso", que la tabla marca junto al Tipo,
// "Rescate Total", que muestra en Monto, y "Fecha Programada", que muestra
// junto al Estado).
//
// Criterios:
// - Importes, cantidades y contadores van como números, sin formatear, para
//   que Excel pueda sumarlos y filtrarlos.
// - Las fechas van como texto DD/MM/AAAA. No como Date: `json_to_sheet` las
//   convierte a serial usando UTC y en GMT-3 quedan corridas un día (una
//   instrucción del 03/09 se exportaba como 02/09). El texto no se corre.
//
// - Una transferencia de varias especies sale en una fila por especie, con el
//   mismo ID: así Cantidad sigue siendo un número por especie y la planilla se
//   puede filtrar por especie.
//
// El orden de las claves define el orden de las columnas: `exportRowsToXlsx`
// usa XLSX.utils.json_to_sheet, que toma los encabezados del primer objeto.

import { formatComitenteForDisplay } from './formatters';
import { formatDate, fromIsoDate } from './dateHelpers';
import { getTitulosInstruccion } from './instruccionesTitulos';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** DD/MM/AAAA a partir de un Timestamp de Firestore, un Date o un 'YYYY-MM-DD'. */
export const formatFechaExport = (value) => {
    if (!value) return '';
    // Las claves 'YYYY-MM-DD' se parsean como local para no correr el día.
    if (typeof value === 'string') return ISO_DATE.test(value) ? formatDate(fromIsoDate(value)) : value;
    const date = typeof value.toDate === 'function' ? value.toDate() : value;
    if (!(date instanceof Date) || isNaN(date.getTime())) return '';
    return formatDate(date);
};

/** Número para Excel; vacío si el campo no aplica (no 0, que sí significa algo). */
const toNumber = (value) => {
    if (value === null || value === undefined || value === '') return '';
    const parsed = parseFloat(value);
    return isNaN(parsed) ? '' : parsed;
};

export const buildInstruccionExportRow = (instr = {}) => ({
    ID: instr.persistentId ?? '',
    Fecha: formatFechaExport(instr.fechaOperativa),
    'Fecha de Carga': formatFechaExport(instr.createdAt),
    Tipo: instr.tipo || '',
    // Solo los Egresos tienen vía; los cargados antes del campo fueron transferencias normales.
    'Vía Egreso': instr.tipo === 'Egreso' ? (instr.viaEgreso || 'Transferencia normal') : '',
    'Nro Comitente': formatComitenteForDisplay(instr.nroComitente) || '',
    Cliente: instr.cliente || '',
    // En un rescate total el monto lo define la sociedad gerente: no hay importe
    // cargado, y la marca va en su propia columna.
    Monto: instr.esRescateTotal ? '' : toNumber(instr.monto),
    'Rescate Total': instr.esRescateTotal ? 'Sí' : '',
    Cantidad: toNumber(instr.cantidad),
    Especie: instr.especie || '',
    FCI: instr.fci || '',
    'Plazo Liq.': instr.plazoLiquidacion || '',
    'Fecha Liq.': formatFechaExport(instr.fechaLiquidacion),
    Contraparte: instr.contraparte || '',
    Moneda: instr.moneda || '',
    Comercial: instr.comercial || '',
    'Middle Office': instr.middleOffice || '',
    Estado: instr.estado || '',
    'Fecha Programada': formatFechaExport(instr.fechaProgramada),
    'Com. Comercial': instr.comentarioComercial || '',
    'Com. Middle Office': instr.comentarioMiddleOffice || '',
    Adjuntos: Array.isArray(instr.adjuntos) ? instr.adjuntos.length : 0,
});

export const buildInstruccionesExportRows = (instrucciones = []) =>
    instrucciones.flatMap((instr) => {
        const row = buildInstruccionExportRow(instr);
        const titulos = getTitulosInstruccion(instr);
        if (titulos.length <= 1) return [row];
        return titulos.map(({ especie, cantidad }) => ({ ...row, Cantidad: toNumber(cantidad), Especie: especie }));
    });

/** Anchos alineados con el orden de `buildInstruccionExportRow`. */
export const ANCHOS_COLUMNAS_INSTRUCCIONES = [
    6, 12, 14, 22, 20, 14, 30, 16, 13, 14, 20, 24, 11, 12, 24, 11, 18, 18, 12, 16, 34, 34, 10,
];
